import type { SwapToken } from './types'

/**
 * Every address the Swap page talks to on Base (chain id 8453).
 *
 * Each one was called on 2026-09-02 from a script against
 * `https://mainnet.base.org` and answered as scope 13 §2.2 records: the
 * routers agree with their factories, the factories return the pools named
 * there, and the quoters price 1 WETH into USDC within a few dollars of each
 * other. They are public, permanent addresses of contracts nobody here
 * deploys, so they live in code rather than in a dashboard setting somebody
 * has to remember — and they are not "corrected" without re-running that
 * check.
 */

export const BASE_CHAIN_ID = 8453

/** ETH is not an ERC-20; the page carries it as this sentinel and swaps WETH on chain. */
export const NATIVE_SENTINEL = '0x0000000000000000000000000000000000000000'

// ── Tokens ──────────────────────────────────────────────────────────────────

export const WETH = '0x4200000000000000000000000000000000000006'
export const USDC = '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913'
/** Bridged USDC. Still one of the deeper Aerodrome pairs, so it earns a hub slot. */
export const USDBC = '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca'
export const DAI = '0x50c5725949a6f0c72e6c4a641f24049a917db0cb'
export const CBBTC = '0xcbb7c0000ab88b473b1f5afd9ef808440eed33bf'
export const AERO = '0x940181a94a35a4569e4529a3cdfb74e38fd98631'

/**
 * MPRO on Base. Documentation only — the page reads the live address from the
 * platform's own contract configuration (`useMproTokenContractAddress`), so a
 * redeploy needs no release here.
 */
export const MPRO_DOCUMENTED = '0xd88611a629265c9af294ffdd2e7fa4546612273e'

/** Master distributor: decides how much of an MPRO transfer is burned (§2.4). */
export const MPRO_MASTER_DISTRIBUTOR = '0x568cd600a92b26f848804e4005822f7d28753632'

// ── Venues ──────────────────────────────────────────────────────────────────

export const AERO_ROUTER = '0xcf77a3ba9a5ca399b7c97c74d54e5b1beb874e43'
export const AERO_POOL_FACTORY = '0x420dd381b31aef6683db6b902084cb0ffece40da'

export const CL_FACTORY = '0x5e7bb104d84c7cb9b682aac2f3d509f5f406809a'
export const CL_SWAP_ROUTER = '0xbe6d8f0d05cc4be24d5167a3ef062215be6d18a5'
export const CL_QUOTER = '0x254cf9e1e6e233aa1ac962cb9b05b2cfeaae15b0'

/**
 * Slipstream factory 3 — the same code as factory 1 at a different deployment,
 * and the venue Coinbase's tokenized stocks actually trade on.
 *
 * Scope 13 recorded it as phase B and did not probe it. Measured on 2026-09-11
 * (scope 40 §1.5): AAPLc/USDC here holds 1 057 610 USDC against 1 954 AAPLc,
 * while the same pair on Uniswap v3 holds 952 USDC. Leaving it out did not mean
 * a stock could not be swapped — it meant the page would rank a thousand-dollar
 * pool as the best route it could find, which is worse than finding none.
 *
 * Each address answered on 2026-09-11: the pool's own `factory()` is the
 * factory below, and both periphery contracts answer `factory()` with it.
 */
export const CL3_FACTORY = '0xf8f2eb4940cfe7d13603dddd87f123820fc061ef'
export const CL3_SWAP_ROUTER = '0x698cb2b6dd822994581fea6ea4fc755d1363a92f'
export const CL3_QUOTER = '0x514c8b5f54112481e28028f1166bd78501089259'

export const UNI_V3_FACTORY = '0x33128a8fc17869897dce68ed026d694621f6fdfd'
export const UNI_V3_QUOTER = '0x3d4e44eb1374240ce5f1b871ab261cd16335b76a'
export const UNI_SWAP_ROUTER02 = '0x2626664c2603336e57b271c5c0b26f421741e481'

export const UNI_V2_FACTORY = '0x8909dc15e40173ff4699343b6eb8132c65e18ec6'
export const UNI_V2_ROUTER = '0x4752ba5dbc23f44d87826276bf6fd6b1c372ad24'

export const MULTICALL3 = '0xca11bde05977b3631167028862be2a173976ca11'

// ── Pool variants probed during discovery ───────────────────────────────────

export const UNI_V3_FEES = [100, 500, 3000, 10000] as const

/**
 * `tickSpacings()` on the Slipstream factory answered [1, 50, 100, 200, 2000, 10]
 * on 2026-09-02; sorted here because the order only decides the order of the
 * multicall rows, and a sorted list reads.
 */
export const CL_TICK_SPACINGS_F1 = [1, 10, 50, 100, 200, 2000] as const

/**
 * Factory 3 answered `tickSpacings()` with the same six plus **500** on
 * 2026-09-11, and 500 is not decoration: GOOGLc/USDC uses it. Probing 500 on
 * factory 1 would cost one multicall row per pair for a pool that factory
 * cannot create, so the two lists stay separate.
 */
export const CL_TICK_SPACINGS_F3 = [1, 10, 50, 100, 200, 500, 2000] as const

// ── Hubs ────────────────────────────────────────────────────────────────────

/**
 * The tokens a two-hop route may pass through. Kept short on purpose: the
 * discovery multicall is `1 + 2·|hubs|` pairs wide, so every hub costs
 * twenty-six calls whether or not it has a pool.
 */
export const HUB_TOKENS: SwapToken[] = [
  { address: WETH, symbol: 'WETH', decimals: 18, isNative: false, isMpro: false },
  { address: USDC, symbol: 'USDC', decimals: 6, isNative: false, isMpro: false },
  { address: USDBC, symbol: 'USDbC', decimals: 6, isNative: false, isMpro: false },
  { address: DAI, symbol: 'DAI', decimals: 18, isNative: false, isMpro: false },
  { address: CBBTC, symbol: 'cbBTC', decimals: 8, isNative: false, isMpro: false },
  { address: AERO, symbol: 'AERO', decimals: 18, isNative: false, isMpro: false }
]

export const ETH_TOKEN: SwapToken = {
  address: NATIVE_SENTINEL,
  symbol: 'ETH',
  decimals: 18,
  isNative: true,
  isMpro: false
}
